import type { FC, ReactNode } from 'react'
import type { DoorConfig } from '@/lib/configurator/options'
import type { Rect } from '@/lib/configurator/layout'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { computeLayout } from '@/lib/configurator/layout'
import { finishColor, findHandleFinish } from '@/lib/configurator/options'
import { GLASS_LOOK_FILL, glassLook } from '@/lib/configurator/appearance'

interface DoorDrawingProps {
    config: DoorConfig
    className?: string
    showDimensions?: boolean
    title?: string
}

const DoorDrawing: FC<DoorDrawingProps> = ({ config, className, showDimensions = false, title }): ReactNode => {
    const layout = useMemo(() => computeLayout(config), [config])

    const look = glassLook(config)
    const handleFinish = findHandleFinish(config.handleFinish)

    const pad = showDimensions ? 260 : 60
    const { bounds } = layout
    const minX = bounds.x - pad
    const maxY = bounds.y + bounds.h + 60
    const width = bounds.w + pad + 60
    const height = bounds.h + 60 + pad

    const sy = (y: number) => maxY - y
    const rect = (r: Rect, props: Record<string, string | number> = {}, key?: string) => (
        <rect key={key} x={r.x} y={sy(r.y + r.h)} width={Math.max(0, r.w)} height={Math.max(0, r.h)} {...props} />
    )

    const steel = finishColor(config)
    const lightSteel = config.finish === 'ral9016'
    const glassFill = GLASS_LOOK_FILL[look]
    const glassOpacity = look === 'rook' || look === 'brons' ? 0.82 : 0.65
    const strokeWidth = Math.max(4, bounds.w / 400)
    const steelProps = lightSteel ? { fill: steel, stroke: '#9a9a9a', strokeWidth: 2 } : { fill: steel }

    const handleNodes = layout.leaves.map(leaf => {
        const { handle } = leaf
        const color = handleFinish.color

        if (config.handle === 'kruk' || config.handle === 'vaste-kruk') {
            const direction = handle.edge === 'right' ? -1 : 1

            return (
                <g key={`${leaf.key}-handle`}>
                    {config.handle === 'kruk'
                        ? <circle cx={handle.x} cy={sy(handle.y)} r={30} fill={color} />
                        : <rect x={handle.x - 28} y={sy(handle.y) - 28} width={56} height={56} fill={color} />}
                    <rect x={direction === -1 ? handle.x - handle.length : handle.x} y={sy(handle.y) - 10} width={handle.length} height={20} fill={color} />
                </g>
            )
        }

        const thickness = config.handle === 'lang' ? 22 : 20

        return (
            <rect
                key={`${leaf.key}-handle`}
                x={handle.x - thickness / 2}
                y={sy(handle.y + handle.length / 2)}
                width={thickness}
                height={handle.length}
                fill={color}
                stroke='#00000033'
                strokeWidth={3}
            />
        )
    })

    const overallWidth = layout.sliding ? config.width : layout.width
    const overallHeight = layout.sliding ? config.height : layout.height
    const dimFont = Math.max(70, bounds.w / 22)

    return (
        <svg
            viewBox={`${minX} 0 ${width} ${height}`}
            className={cn('h-auto w-full', className)}
            role='img'
            aria-label={title ?? 'Technische tekening van de deur'}
        >
            {layout.sliding && (
                <g>
                    {rect({ x: 0, y: 0, w: config.width, h: config.height }, { fill: 'none', stroke: '#9b958c', strokeWidth, strokeDasharray: '30 22' })}
                    {layout.rail && rect(layout.rail, steelProps)}
                </g>
            )}

            {layout.panels.map(panel => rect(panel.glass, { fill: glassFill, fillOpacity: glassOpacity }, `${panel.key}-glass`))}
            {layout.leaves.map(leaf => rect(leaf.glass, { fill: glassFill, fillOpacity: glassOpacity }, `${leaf.key}-glass`))}

            {look === 'ribbel' && [...layout.leaves.map(leaf => leaf.glass), ...layout.panels.map(panel => panel.glass)].map((g, index) => (
                <g key={`ribbel-${index}`} opacity={0.35}>
                    {Array.from({ length: Math.floor(g.w / 40) }, (_, i) => (
                        <line key={i} x1={g.x + 20 + i * 40} x2={g.x + 20 + i * 40} y1={sy(g.y)} y2={sy(g.y + g.h)} stroke='#ffffff' strokeWidth={8} />
                    ))}
                </g>
            ))}

            {[...layout.leaves.map(leaf => leaf.glass), ...layout.panels.map(panel => panel.glass)].map((g, index) => (
                <line
                    key={`reflect-${index}`}
                    x1={g.x + g.w * 0.15}
                    y1={sy(g.y + g.h * 0.55)}
                    x2={g.x + g.w * 0.55}
                    y2={sy(g.y + g.h * 0.95)}
                    stroke='#ffffff'
                    strokeOpacity={0.45}
                    strokeWidth={strokeWidth * 2}
                />
            ))}

            {layout.frame.map((member, index) => rect(member, steelProps, `frame-${index}`))}

            {layout.leaves.map(leaf => (
                <g key={leaf.key}>
                    {leaf.members.map((member, index) => rect(member, steelProps, `${leaf.key}-m-${index}`))}
                    {leaf.bottomPanel && rect(leaf.bottomPanel, { ...steelProps, fillOpacity: 0.92 })}
                    {leaf.bars.map((bar, index) => rect(bar, steelProps, `${leaf.key}-b-${index}`))}
                    {leaf.pivotX !== null && (
                        <line x1={leaf.pivotX} x2={leaf.pivotX} y1={sy(leaf.rect.y)} y2={sy(leaf.rect.y + leaf.rect.h)} stroke='#f26a1b' strokeWidth={strokeWidth} strokeDasharray='24 18' opacity={0.8} />
                    )}
                </g>
            ))}

            {layout.panels.map(panel => panel.bars.map((bar, index) => rect(bar, steelProps, `${panel.key}-b-${index}`)))}

            {handleNodes}

            <line x1={minX + 20} x2={minX + width - 20} y1={sy(0)} y2={sy(0)} stroke='#8e887f' strokeWidth={strokeWidth} />

            {showDimensions && (
                <g fill='#57524b' stroke='#57524b' fontFamily='inherit'>
                    <line x1={layout.sliding ? 0 : 0} x2={overallWidth} y1={sy(-150)} y2={sy(-150)} strokeWidth={strokeWidth} />
                    <line x1={0} x2={0} y1={sy(-110)} y2={sy(-190)} strokeWidth={strokeWidth} />
                    <line x1={overallWidth} x2={overallWidth} y1={sy(-110)} y2={sy(-190)} strokeWidth={strokeWidth} />
                    <text x={overallWidth / 2} y={sy(-150) - 30} textAnchor='middle' fontSize={dimFont} stroke='none'>
                        {`${Math.round(overallWidth).toLocaleString('nl-NL')} mm`}
                    </text>

                    <line x1={minX + 110} x2={minX + 110} y1={sy(0)} y2={sy(overallHeight)} strokeWidth={strokeWidth} />
                    <line x1={minX + 70} x2={minX + 150} y1={sy(0)} y2={sy(0)} strokeWidth={strokeWidth} />
                    <line x1={minX + 70} x2={minX + 150} y1={sy(overallHeight)} y2={sy(overallHeight)} strokeWidth={strokeWidth} />
                    <text
                        x={minX + 80}
                        y={sy(overallHeight / 2)}
                        textAnchor='middle'
                        fontSize={dimFont}
                        stroke='none'
                        transform={`rotate(-90 ${minX + 80} ${sy(overallHeight / 2)})`}
                    >
                        {`${Math.round(overallHeight).toLocaleString('nl-NL')} mm`}
                    </text>
                </g>
            )}
        </svg>
    )
}

export default DoorDrawing
